import { createSlice } from '@reduxjs/toolkit';

// ⚠️ These keys are NOT renamed when the product is renamed: changing them
// signs out every existing user, because their stored session becomes
// unreadable. They are invisible to users; the cost of "tidying" them is real.
const TOKEN_KEY = 'commissioner.accessToken';
const USER_KEY = 'commissioner.user';
const REFRESH_KEY = 'commissioner.refreshToken';

// Hydrate from localStorage so a page refresh keeps the user logged in.
// Note: localStorage is OK for a SaaS dev build but should move to httpOnly
// cookies before production — XSS protection.
const loadInitial = () => {
    try {
        const token = localStorage.getItem(TOKEN_KEY);
        const userJson = localStorage.getItem(USER_KEY);
        if (token && userJson) {
            return {
                accessToken: token,
                refreshToken: localStorage.getItem(REFRESH_KEY),
                user: JSON.parse(userJson),
            };
        }
    } catch {
        // Ignore — fall through to logged-out state.
    }
    return { accessToken: null, refreshToken: null, user: null };
};

const initialState = {
    ...loadInitial(),
    status: 'idle',  // 'idle' | 'loading' | 'failed'
    error: null,
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        loginStart(state) {
            state.status = 'loading';
            state.error = null;
        },
        loginSuccess(state, action) {
            const { accessToken, refreshToken, ...user } = action.payload;
            state.accessToken = accessToken;
            state.refreshToken = refreshToken ?? null;
            state.user = user;
            state.status = 'idle';
            state.error = null;
            try {
                localStorage.setItem(TOKEN_KEY, accessToken);
                localStorage.setItem(USER_KEY, JSON.stringify(user));
                if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
            } catch {
                /* localStorage disabled — auth still works for this session */
            }
        },

        /**
         * A renewed session. Deliberately separate from loginSuccess: renewal
         * must not touch `status` or `error`, because it happens underneath
         * whatever the user is doing and should be invisible to the UI.
         *
         * Both tokens are replaced — renewal rotates the refresh token, and
         * keeping the old one would present a spent token next time, which the
         * server treats as theft and answers by ending the session.
         */
        sessionRenewed(state, action) {
            const { accessToken, refreshToken } = action.payload;
            state.accessToken = accessToken;
            if (refreshToken) state.refreshToken = refreshToken;
            try {
                localStorage.setItem(TOKEN_KEY, accessToken);
                if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
            } catch {
                /* ignore */
            }
        },
        loginFailure(state, action) {
            state.status = 'failed';
            state.error = action.payload;
            state.accessToken = null;
            state.refreshToken = null;
            state.user = null;
        },
        logout(state) {
            state.accessToken = null;
            state.refreshToken = null;
            state.user = null;
            state.status = 'idle';
            state.error = null;
            try {
                localStorage.removeItem(TOKEN_KEY);
                localStorage.removeItem(USER_KEY);
                localStorage.removeItem(REFRESH_KEY);
            } catch {
                /* ignore */
            }
        },
    },
});

export const { loginStart, loginSuccess, sessionRenewed, loginFailure, logout } =
    authSlice.actions;

// Selectors
export const selectIsAuthenticated = (state) => Boolean(state.auth.accessToken);
export const selectUser = (state) => state.auth.user;
export const selectAuthStatus = (state) => state.auth.status;
export const selectAuthError = (state) => state.auth.error;

export default authSlice.reducer;
